'use client';

import { useTranslations } from 'next-intl';
import type { TooltipRenderProps } from 'react-joyride';

/**
 * Custom tooltip that renders react-joyride steps with the app's semantic
 * color tokens (no hardcoded Tailwind colors) so it matches both light and
 * dark themes automatically. Spreads the render-props button props so joyride's
 * ARIA/keyboard support is preserved.
 */
export function TourTooltip({
  backProps,
  closeProps,
  continuous,
  index,
  isLastStep,
  primaryProps,
  skipProps,
  step,
  tooltipProps,
}: TooltipRenderProps) {
  const t = useTranslations('Tour');

  // Steps reference i18n keys (title/content), never raw strings. joyride
  // types these fields as ReactNode, so coerce to string for the untyped
  // t.raw call — our steps config always supplies valid dot keys under Tour
  // (e.g. "title.welcome" → t.raw("title.welcome")).
  const title = step.title
    ? t.raw(String(step.title) as Parameters<typeof t.raw>[0])
    : null;
  const content = step.content
    ? t.raw(String(step.content) as Parameters<typeof t.raw>[0])
    : null;

  return (
    <div
      {...tooltipProps}
      className='relative w-80 max-w-[calc(100vw-2rem)] rounded-2xl border border-line-default bg-surface-card p-5 text-content-primary shadow-xl'
    >
      <button
        {...closeProps}
        // 44x44 minimum touch target (#5942) — the icon stays visually small,
        // the hit area does not.
        className='absolute right-1 top-1 flex h-11 w-11 items-center justify-center rounded-full text-content-muted hover:text-content-secondary'
        aria-label={t('close')}
      >
        ✕
      </button>
      {title && (
        <h3 className='mb-2 pr-8 text-base font-semibold text-content-primary'>
          {title}
        </h3>
      )}
      <p className='text-sm leading-relaxed text-content-secondary'>
        {content}
      </p>
      <div className='mt-5 flex items-center gap-2'>
        <button
          {...skipProps}
          className='min-h-11 rounded-full px-4 py-2 text-sm text-content-muted hover:text-content-secondary'
        >
          {t('skip')}
        </button>
        {index > 0 && (
          <button
            {...backProps}
            className='min-h-11 rounded-full px-4 py-2 text-sm text-content-secondary hover:bg-brand-tertiary/10'
          >
            {t('back')}
          </button>
        )}
        {continuous && (
          <button
            {...primaryProps}
            className='ml-auto min-h-11 rounded-full bg-brand-primary px-5 py-2 text-sm font-medium text-content-inverse shadow-sm hover:bg-brand-primary/90'
          >
            {isLastStep ? t('finish') : t('next')}
          </button>
        )}
      </div>
    </div>
  );
}
